import { Line, Point } from "types/canvasTypes";

export const calculateLineLength = (line: Line, scale: number): number => {
  const { start, end } = line;
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  // Apply Pythagorean theorem to calculate the length
  const length = Math.sqrt(dx * dx + dy * dy);

  // round to 2 decimals
  return Math.round(length * 100) / 100 / scale;
};

export const calculateAngle = (line: Line) => {
  const { start, end } = line;
  const dx = end.x - start.x;
  const dy = end.y - start.y;

  const currentAngle = Math.atan2(dy, dx) * (180 / Math.PI);

  return Math.round(currentAngle * 100) / 100;
};

export const connectLines = (start: Point, lines: Line[]): Point => {
  const threshold = 15;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (
      Math.abs(line.start.x - start.x) < threshold &&
      Math.abs(line.start.y - start.y) < threshold
    ) {
      return { x: line.start.x, y: line.start.y };
    }

    if (
      Math.abs(line.end.x - start.x) < threshold &&
      Math.abs(line.end.y - start.y) < threshold
    ) {
      return { x: line.end.x, y: line.end.y };
    }
  }

  return start;
};

export const modifyEndPoint = (line: Line) => {
  const currentAngle = line.angle;
  if (currentAngle === undefined) {
    return line;
  }
  let desiredAngle = currentAngle;

  if (currentAngle >= 80 && currentAngle <= 100) {
    desiredAngle = 90; // Desired angle in degrees
  }
  if (currentAngle >= 170 && currentAngle <= 190) {
    desiredAngle = 180; // Desired angle in degrees
  }
  if (currentAngle >= -10 && currentAngle <= 10) {
    desiredAngle = 0; // Desired angle in degrees
  }
  if (currentAngle >= -100 && currentAngle <= -80) {
    desiredAngle = -90; // Desired angle in degrees
  }
  if (currentAngle >= -190 && currentAngle <= -170) {
    desiredAngle = -180; // Desired angle in degrees
  }

  if (desiredAngle === currentAngle) {
    return line;
  }

  const angleDifference = desiredAngle - currentAngle;
  const angleDifferenceInRadians = angleDifference * (Math.PI / 180);

  const dx = line.end.x - line.start.x;
  const dy = line.end.y - line.start.y;
  const lineLength = Math.sqrt(dx * dx + dy * dy);

  const angle = Math.atan2(dy, dx);
  const newAngle = angle + angleDifferenceInRadians;

  const correctedDx = lineLength * Math.cos(newAngle);
  const correctedDy = lineLength * Math.sin(newAngle);

  line.end.x = line.start.x + correctedDx;
  line.end.y = line.start.y + correctedDy;
  line.angle = desiredAngle;

  return line;
};

export function drawParallelLine(line: Line, flipLine: boolean) {
  const angle = line.angle;
  if (angle === undefined) {
    return line;
  }
  const { start, end } = line;

  const angleInRadians = angle * (Math.PI / 180);

  // Calculate the perpendicular offset
  let constant = 17;

  if (flipLine) {
    constant = constant * -1;
  }

  const offset = constant + Math.sin(angleInRadians);
  // Calculate the coordinates of the parallel line's start point
  const parallelStartX =
    start.x + offset * Math.cos(angleInRadians + Math.PI / 2);
  const parallelStartY =
    start.y + offset * Math.sin(angleInRadians + Math.PI / 2);

  // Calculate the coordinates of the parallel line's end point
  const parallelEndX = end.x + offset * Math.cos(angleInRadians + Math.PI / 2);
  const parallelEndY = end.y + offset * Math.sin(angleInRadians + Math.PI / 2);

  const newLine = {
    start: line.start,
    end: line.end,
    angle: line.angle,
    parallelLine: {
      start: { x: parallelStartX, y: parallelStartY },
      end: { x: parallelEndX, y: parallelEndY },
      numberOfDoors: line.numberOfDoors,
    },
    numberOfDoors: line.numberOfDoors,
  };

  return newLine;
}

// this is a function to check if the line is valid. If the line is too short, it will return false
export const isLineValid = (line: Line): Boolean => {
  if (
    Math.abs(line.start.x - line.end.x) < 16 &&
    Math.abs(line.start.y - line.end.y) < 16
  ) {
    return false;
  }
  return true;
};
